import { ReadonlyVec3 } from 'gl-matrix';
import { gl } from '../../../Engine';
import { StructureSetObject } from '../../../data/structure/StructureSetObject';
import { TextureView } from '../../../view/TextureView';
import { PlaneView } from '../../../view/views/PlaneView';
import { ShaderProgram } from '../../utils/ShaderProgram';
import { UniformFloat } from '../../utils/uniforms/UniformFloat';
import { UniformVec4 } from '../../utils/uniforms/UniformVec4';
import { Renderer } from '../Renderer';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { contourEdgeShaderChunk, crossSectionVertexShader } from './glsl';
import { ContourEdgeUniforms } from './util/contourEdgeUniforms';
import { resolveContourPass } from '~/engine/view/layers/contourAlpha';

/** Per-draw options for contour rendering. */
export interface ContourRenderOptions {
    /**
     * Color alpha (0..1). Default 1.0.
     * Passed as the alpha component of colorMask and used as an
     * RGB multiplier in the shader so the contour stays fully
     * opaque in the layer FBO but appears visually dimmer.
     */
    alpha?: number;

    /**
     * Interior fill alpha (0..1). Default 0.0 (no fill).
     * When > 0 the SDF interior is rendered with this alpha,
     * giving a translucent highlight of the region area.
     */
    fillAlpha?: number;

    /**
     * Omit the SELECTED structure from this pass entirely. Default false.
     *
     * Not an alpha of zero: the shader folds alpha into RGB and then writes
     * `vec4(color, 1.0)`, so alpha 0 draws a fully OPAQUE BLACK contour
     * exactly where the structure is. Only omission removes it from the
     * frame.
     *
     * Set it for a preview that RE-DRAWS the selected structure itself, so
     * the "before" and the "after" are not on screen together and a slice
     * the operation empties renders as empty.
     */
    hideSelected?: boolean;

    /**
     * Apply {@link alpha} only while the pass has a selected structure to dim
     * around: one it draws, or one {@link peekedRoiNumber} withholds. Set by
     * a mode that spotlights the selection: with nothing held at full
     * intensity, every contour fading at once reads as missing contours
     * rather than as dimmed ones.
     */
    dimsOnlyAroundSelection?: boolean;

    /**
     * The ROI whose contour this pass leaves out WHILE IT IS THE SELECTED
     * structure. Default: none. A number that names any other structure
     * leaves nothing out.
     *
     * Separate from {@link hideSelected}, which says a preview re-draws the
     * selected structure and so removes it as the thing to dim around. A
     * peeked selection is only withheld from view: it still counts as present
     * for {@link dimsOnlyAroundSelection}, so the other contours keep the
     * alpha they had before the peek.
     */
    peekedRoiNumber?: number;
}

export class ContourRenderer implements Renderer {
    private readonly textureShader: ContourTextureShader;

    public constructor() {
        this.textureShader = new ContourTextureShader();
    }

    /**
     * Render all visible structures from the given structure sets
     * on a {@link PlaneView}.
     * @throws Error if the view type is not supported.
     */
    public render(
        view: TextureView,
        structureSets: StructureSetObject[],
        options?: ContourRenderOptions
    ): boolean {
        if (!(view instanceof PlaneView)) {
            throw new Error(
                'ContourRenderer on View of type ' +
                    `${view.constructor} currently not supported.`
            );
        }

        const fillAlpha = options?.fillAlpha ?? 0.0;

        // Phase 1: Collect textures before setting up GL state.
        // Accessing roi.texture may trigger lazy SDF generation
        // (via SdfGenerator.renderSdf), which changes the active
        // GL program. Doing this before shader.start() avoids
        // rendering corruption from an unbound shader.
        const candidates: {
            roiNumber: number;
            texture: ContourTexture;
            color: ReadonlyVec3;
            thickness: number;
            isSelected: boolean;
        }[] = [];

        for (const structureSet of structureSets) {
            for (const roi of structureSet.structures) {
                const texture = roi.drawableTexture;
                if (!texture) {
                    continue;
                }

                const isSelected = roi === structureSet.selectedStructure;
                let contourThickness = roi.contourThickness;
                if (isSelected) {
                    contourThickness *= 2;
                }

                candidates.push({
                    roiNumber: roi.roiNumber,
                    texture,
                    color: roi.color,
                    thickness: contourThickness,
                    isSelected,
                });
            }
        }

        // Every structure with something to draw is known, so the pass can
        // settle which of them it draws and whether a mode that dims around
        // the selection has one to spotlight. A structure it leaves out is
        // never drawn at all — see `hideSelected` for why no alpha hides one.
        const { drawn, dimmedAlpha: alpha } = resolveContourPass(
            options,
            candidates
        );
        const entries = candidates.filter((_, index) => drawn[index]);

        // Phase 2: Render with clean GL state — no SDF
        // generation can occur since all textures are resolved.
        gl.enable(gl.BLEND);
        gl.blendEquation(gl.FUNC_ADD);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
        this.textureShader.start();
        try {
            this.textureShader.fillAlpha.loadFloat(fillAlpha);

            for (const entry of entries) {
                // The selected structure is exempt from the `alpha` recede that
                // applies to the rest: it is the structure being edited, and a
                // preview that needs it out of the way removes it outright
                // (see `hideSelected`) rather than dimming it.
                const entryAlpha = entry.isSelected ? 1.0 : alpha;
                this.drawContour(
                    view,
                    entry.texture,
                    entry.color,
                    entry.thickness,
                    entryAlpha
                );
            }
        } finally {
            this.textureShader.stop();
            gl.disable(gl.BLEND);
        }
        return true;
    }

    /**
     * Render a single contour texture on a PlaneView.
     * Sets up and tears down GL state internally so it
     * can be called as a standalone entry point.
     */
    public renderContourTexture(
        view: TextureView,
        texture: ContourTexture,
        color: ReadonlyVec3,
        contourThickness: number = 1.5,
        options?: ContourRenderOptions
    ): void {
        if (!(view instanceof PlaneView)) {
            return;
        }

        gl.enable(gl.BLEND);
        gl.blendEquation(gl.FUNC_ADD);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
        this.textureShader.start();
        try {
            this.textureShader.fillAlpha.loadFloat(options?.fillAlpha ?? 0.0);
            this.drawContour(
                view,
                texture,
                color,
                contourThickness,
                options?.alpha ?? 1.0
            );
        } finally {
            this.textureShader.stop();
            gl.disable(gl.BLEND);
        }
    }

    /**
     * Internal draw call for a single contour.
     * Assumes the shader is already started and blend is enabled.
     *
     * @param alpha Brightness multiplier passed via colorMask.a
     */
    private drawContour(
        view: PlaneView,
        texture: ContourTexture,
        color: ReadonlyVec3,
        contourThickness: number,
        alpha: number = 1.0
    ): void {
        const vao = texture.crossSection(view.projectionViewMatrix);
        if (!vao) {
            return;
        }
        try {
            this.textureShader.edge.load(
                view.projectionViewMatrix,
                view.size,
                texture,
                contourThickness
            );
            this.textureShader.colorMask.loadVec4(color, alpha);

            texture.bindTexture();
            vao.bind(0, 1);
            gl.drawElements(gl.TRIANGLES, vao.indexCount, gl.UNSIGNED_INT, 0);
        } finally {
            vao.unbind(0, 1);
            vao.delete();
            texture.unbindTexture();
        }
    }

    /**
     * Delete the shader allocated by this renderer.
     * Must be called when the renderer is no longer needed.
     */
    public delete(): void {
        this.textureShader.delete();
    }
}

/**
 * Shader that colours the fragments of an SDF cross-section lying on the
 * contour edge — detected by the shared contour-edge chunk's 8-neighbour
 * test around each fragment — and optionally fills the interior.
 *
 * colorMask.a carries the contour alpha — used as an RGB multiplier
 * so dimmed contours stay fully opaque in the layer FBO.
 *
 * fillAlpha controls interior rendering: when > 0 the SDF interior
 * is drawn with that alpha for a translucent region highlight.
 */
class ContourTextureShader extends ShaderProgram {
    private static readonly FRAGMENT_CONTENT = `#version 300 es

    precision highp float;
    precision highp sampler3D;

    in vec3 pass_textureCoordinates;
    out vec4 outColor;

    uniform vec4 colorMask;
    uniform float fillAlpha;

    ${contourEdgeShaderChunk}

    void main() {
        // Hard clip in Z at the image's first and last slice center
        // positions. The cross-section geometry covers the full
        // texture box, which extends a half-voxel past each end of
        // the clinical Z range.
        if (!isInsideImageZ(pass_textureCoordinates.z)) {
            discard;
        }

        float sdfValue = texture(sampler, pass_textureCoordinates).r;
        bool isInside = sdfValue >= SDF_THRESHOLD;
        bool isEdge = isContourEdge(pass_textureCoordinates, isInside);

        vec3 color = colorMask.rgb * colorMask.a;

        if (isEdge) {
            outColor = vec4(color, 1.0);
        } else if (isInside && fillAlpha > 0.0) {
            outColor = vec4(color, fillAlpha);
        } else {
            discard;
        }
    }`;

    public readonly colorMask: UniformVec4;
    public readonly fillAlpha: UniformFloat;
    public readonly edge: ContourEdgeUniforms;

    constructor() {
        super(
            crossSectionVertexShader,
            ContourTextureShader.FRAGMENT_CONTENT,
            'position',
            'textureCoords'
        );
        this.colorMask = new UniformVec4('colorMask');
        this.fillAlpha = new UniformFloat('fillAlpha');
        this.edge = new ContourEdgeUniforms();
        this.storeUniformLocations(
            this.colorMask,
            this.fillAlpha,
            ...this.edge.all
        );
    }
}
